import React, { useId } from 'react';

const SERVION_MARK_PATHS = [
    'M301.77,0v100.59s0,0,0,0c55.56,0,100.59-45.04,100.59-100.59h0S301.77,0,301.77,0Z',
    'M0,329.03h0s100.59,0,100.59,0v-100.59s0,0,0,0C45.04,228.44,0,273.48,0,329.03Z',
    'M0,60.03v40.56s100.59,100.59,100.59,100.59v-100.59h201.18S201.18,0,201.18,0H68.34c-18.87,0-35.97,7.58-48.42,19.92C9.25,30.42,2.01,44.4,0,60.03Z',
    'M402.21,269v-40.56s-100.59-100.59-100.59-100.59v100.59H100.44s100.59,100.59,100.59,100.59h132.84c18.87,0,35.97-7.58,48.42-19.92,10.66-10.5,17.91-24.48,19.92-40.12Z',
] as const;

const SERVION_TEXT_PATH =
    'M112.85,75.27h-32.57c0-11.24-8.1-18.13-23.2-18.13-13.28,0-22.32,5.24-22.32,13.06,0,9.53,12.01,11.52,25.35,13.78l8.16,1.43c19.51,3.31,48.21,8.21,48.21,37.25,0,24.47-20.5,38.46-57.31,38.46-38.74,0-59.18-15.48-59.18-43.26h32.57c0,12.23,8.54,18.9,26.4,18.9,15.59,0,25.02-5.01,25.02-13.61,0-10.36-11.74-12.45-27.33-15.21l-8.16-1.43c-17.91-3.14-46.23-8.04-46.23-35.65,0-23.42,20.33-37.86,54.17-37.86,35.71,0,56.42,16.09,56.42,42.26Z M244.7,116.65c-6.61,27.66-28.76,44.47-58.3,44.47-36.64,0-61.38-25.9-61.38-64.19s24.47-63.92,60.72-63.92,59.51,25.24,59.51,63.75v8.98h-88.33c2.09,18.18,13.23,28.98,30.42,28.98,12.89,0,21.54-6.06,26.39-18.07h30.97ZM213.57,85.35c-2.76-17.36-12.18-26.39-27.66-26.39s-25.95,9.59-28.65,26.39h56.32Z M286.78,161.12h-33.01V33.01h33.01v45.51c4.35-26.01,16.81-45.51,40.61-45.51h7.22v31.02h-12.73c-27.06,0-35.1,14.33-35.1,38.3v58.79Z M470.21,33.01l-40.89,128.11h-45.74l-40.45-128.11h34.71l29.04,107.06,29.37-107.06h33.94Z M511.74,161.12h-33.01V33.01h33.01v128.11ZM478.74,0v33.01s0,0,0,0c18.23,0,33.01-14.78,33.01-33.01h0s-33.01,0-33.01,0ZM581.88,33.01c36.31,0,61.6,25.29,61.6,64.03s-25.29,64.08-61.6,64.08-61.6-25.35-61.6-64.08,25.29-64.03,61.6-64.03ZM581.88,132.36c17.91,0,28.38-12.84,28.38-35.32s-10.47-35.27-28.38-35.27-28.38,12.78-28.38,35.27,10.52,35.32,28.38,35.32ZM685.02,161.12h-33.01V33.01h33.01v45.63c5.12-25.73,15.47-45.63,42.7-45.63s40.33,19.07,40.33,54.22v73.89h-33.01v-68.38c0-20.72-7.77-31.02-23.58-31.02-16.86,0-26.45,11.46-26.45,32.35v67.06Z';

type Props = React.SVGProps<SVGSVGElement> & {
    title?: string;
    width?: number | string;
    height?: number | string;
    primaryColor?: string;
    textVariant?: 'solid' | 'gradient';
    textColor?: string;
    /** Override the text's vertical position; omitted keeps the centered layout. */
    textOffsetY?: number;
    textGradientFrom?: string;
    textGradientTo?: string;
};

const ServionWordmark: React.FC<Props> = ({
    title = 'Servion wordmark',
    width = 1080,
    height = 193.84,
    primaryColor = '#7edcb1',
    textVariant = 'solid',
    textColor = '#1a1a1a',
    textOffsetY,
    textGradientFrom = '#263f45',
    textGradientTo = '#44776f',
    className,
    ...svgProps
}) => {
    const OUT_W = 1080;
    const OUT_H = 193.84;

    const MARK_W0 = 402.21;
    const MARK_H0 = 329.03;

    const TEXT_W0 = 768.06;
    const TEXT_H0 = 161.12;

    const markScale = OUT_H / MARK_H0;
    const markW = MARK_W0 * markScale;
    const gap = OUT_W - markW - TEXT_W0;
    const textX = markW + gap;
    const textY = textOffsetY ?? (OUT_H - TEXT_H0) / 2;

    const id = useId();
    const textGradientId = `servion-wordmark-text-grad-${id}`;
    const computedTextFill =
        textVariant === 'gradient' ? `url(#${textGradientId})` : textColor;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${OUT_W} ${OUT_H}`}
            width={width}
            height={height}
            role="img"
            aria-label={title}
            className={className}
            {...svgProps}
        >
            {title ? <title>{title}</title> : null}

            {textVariant === 'gradient' ? (
                <defs>
                    <linearGradient
                        id={textGradientId}
                        x1="0"
                        y1="80.56"
                        x2="768.06"
                        y2="80.56"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop offset="0" stopColor={textGradientFrom} />
                        <stop offset="1" stopColor={textGradientTo} />
                    </linearGradient>
                </defs>
            ) : null}

            <g transform={`scale(${markScale})`}>
                {SERVION_MARK_PATHS.map((d) => (
                    <path key={d} d={d} fill={primaryColor} />
                ))}
            </g>

            <g transform={`translate(${textX} ${textY})`}>
                <path d={SERVION_TEXT_PATH} fill={computedTextFill} />
            </g>
        </svg>
    );
};

export default ServionWordmark;
